import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getToken, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export const HOME_BY_ROLE = {
  participant: "/credencial",
  company: "/empresa/portal",
  upv_admin: "/dashboard",
  upv_staff: "/acceso",
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(!!getToken());
  const [meta, setMeta] = useState(null);
  const [settings, setSettings] = useState({ optionA: true, optionB: true, postEvent: true, postEventOpen: true });
  const reloadSettings = useCallback(() => api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}), []);

  const refresh = useCallback(async () => {
    if (!getToken()) { setSession(null); setLoading(false); return null; }
    try {
      const { data } = await api.get("/auth/me");
      setSession(data);
      return data;
    } catch (e) {
      if (e?.response?.status === 401) { setToken(null); setSession(null); }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => setMeta({}));
    reloadSettings();
    const tick = () => { if (document.visibilityState === "visible") reloadSettings(); };
    const sid = setInterval(tick, 10000);
    window.addEventListener("focus", reloadSettings);
    document.addEventListener("visibilitychange", tick);
    const onExpired = () => setSession(null);
    window.addEventListener("upv-session-expired", onExpired);
    return () => { window.removeEventListener("upv-session-expired", onExpired); window.removeEventListener("focus", reloadSettings); document.removeEventListener("visibilitychange", tick); clearInterval(sid); };
  }, [refresh, reloadSettings]);

  const startSession = useCallback((data) => {
    setToken(data.token);
    setSession({ user: data.user, participant: data.participant, company: data.company });
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setSession(data);
    return data;
  }, []);

  const logout = useCallback(() => { setToken(null); setSession(null); }, []);
  const setParticipant = useCallback((participant) => setSession((s) => (s ? { ...s, participant } : s)), []);

  const value = useMemo(() => ({
    loading, meta, settings, reloadSettings, setSettings,
    user: session?.user || null, participant: session?.participant || null, company: session?.company || null,
    login, logout, startSession, refresh, setParticipant,
  }), [loading, meta, settings, reloadSettings, session, login, logout, startSession, refresh, setParticipant]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
